export interface Holiday {
  dateStr: string;
  name: string;
  category: 'secular' | 'christian' | 'islamic' | 'jewish' | 'other_religions' | 'international' | 'development';
  description?: string;
  wikiUrl?: string;
}

function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
}

export function getHolidaysForYear(year: number): Holiday[] {
  const holidays: Holiday[] = [];

  // Descriptions Map
  const desc: Record<string, string> = {
    'Nationalfeiertag': 'Österreichischer Nationalfeiertag; erinnert an die beschlossene immerwährende Neutralität im Jahr 1955.',
    'Silvester': 'Jahresendfeier am 31. Dezember; traditionell gefeiert mit Bleigießen, Feuerwerk und Sekt zur Begrüßung des neuen Jahres.',
    'Josefstag (Landespatron Tirol)': 'Feiertag zu Ehren des Hl. Josef, dem Landespatron von Tirol. Traditionell ein wichtiger Tag für Handwerker und Familien.',
    
    'Heilige Drei Könige': 'Fest der Erscheinung des Herrn; erinnert an den Besuch der Weisen aus dem Morgenland bei der Krippe. Sternsinger sammeln für wohltätige Zwecke.',
    'Mariä Himmelfahrt': 'Feiertag zur Aufnahme Mariens in den Himmel; oft verbunden mit Kräuterweihen und Prozessionen.',
    'Allerheiligen': 'Gedenktag für alle Heiligen der Kirche; Gräberbesuche und Entzünden von Seelenlichtern sind üblich.',
    'Allerseelen': 'Gedenktag für alle Verstorbenen; Familien schmücken Gräber und beten für die Seelen der Verstorbenen.',
    'Mariä Empfängnis': 'Fest der ohne Erbsünde empfangenen Jungfrau Maria; in Österreich ein gesetzlicher Feiertag mit Tradition der Marienfeiern.',
    'Heiliger Abend': 'Vorabend des Weihnachtsfestes; traditionelle Bescherung in der Familie, Besuch der Christmette.',
    'Christtag': 'Hochfest der Geburt Jesu Christi; gefeiert mit festlichen Gottesdiensten und Familienessen.',
    'Stefanitag': 'Gedenktag des Erzmärtyrers Stephanus; traditioneller Tag für Verwandtenbesuche.',
    
    'Karfreitag': 'Gedenktag der Kreuzigung Christi; Tag des Fastens und der Besinnung.',
    'Ostersonntag': 'Auferstehung Jesu Christi; höchstes christliches Fest, gefeiert mit der Osternacht und Ostereiersuchen.',
    'Ostermontag': 'Zweiter Osterfeiertag; erinnert an den Gang der Jünger nach Emmaus.',
    'Christi Himmelfahrt': 'Rückkehr Jesu Christi zum Vater im Himmel, 40 Tage nach Ostern.',
    'Pfingstmontag': 'Entsendung des Heiligen Geistes an die Jünger, Begründung der Kirche.',
    'Fronleichnam': 'Fest des Leibes und Blutes Christi; gefeiert mit feierlichen Prozessionen durch Straßen und Felder.',
    
    'Ramadan (Beginn)': 'Start des islamischen Fastenmonats. Gläubige fasten von der Morgendämmerung bis zum Sonnenuntergang (Enthaltung von Speise, Trank und weltlichen Genüssen).',
    'Eid al-Fitr (Zuckerfest / Ende Ramadan)': 'Fest des Fastenbrechens; dreitägiges Fest zum Ende des Ramadan. Familien feiern mit Festmahlzeiten und Süßigkeiten.',
    'Eid al-Adha (Opferfest)': 'Höchstes islamisches Fest; erinnert an den Propheten Ibrahim und den Gehorsam gegenüber Gott. Teilen des Opferfleischs mit Bedürftigen.',
    'Islamisches Neujahr': 'Erster Tag des Monats Muharram; erinnert an die Auswanderung (Hidschra) des Propheten Mohammed nach Medina.',
    
    'Pessach (Beginn)': 'Erinnert an den Auszug der Israeliten aus Ägypten und die Befreiung aus der Sklaverei. Gefeiert mit dem traditionellen Sederabend.',
    'Jom Kippur': 'Höchster jüdischer Feiertag (Versöhnungstag); geprägt von strengem Fasten, Reue und intensivem Gebet um Vergebung.',
    'Chanukka (Beginn)': 'Jüdisches Lichterfest; erinnert an die Wiedereinweihung des Zweiten Tempels in Jerusalem. Täglich wird eine weitere Kerze am achtarmigen Leuchter entzündet.',
    
    'Vesakh (Buddhismus)': 'Wichtigster buddhistischer Feiertag; feiert Geburt, Erleuchtung und Eingehen ins Parinirvana von Siddhartha Gautama (Buddha).',
    'Diwali (Hinduismus)': 'Hinduistisches Lichterfest; symbolisiert den Sieg des Lichts über die Dunkelheit und des Guten über das Böse. Häuser werden mit Öllampen (Diyas) geschmückt.',
    
    'Neujahr': 'Neujahrstag; Beginn des neuen Kalenderjahres im gregorianischen Kalender.',
    'Staatsfeiertag / Tag der Arbeit': 'Gesetzlicher Feiertag in Österreich; ehrt die Errungenschaften der Arbeiterbewegung und die Bundesverfassung.',
    
    'Opfer-Gedenktag (Holocaust-Gedenktag)': 'Internationaler Gedenktag an die Opfer des Holocaust am Befreiungstag des Konzentrationslagers Auschwitz-Birkenau.',
    'Welttag der sozialen Gerechtigkeit': 'Fördert die Bemühungen zur Armutsbekämpfung, Beschäftigung und sozialen Integration.',
    'Weltfrauentag': 'Tag für die Gleichberechtigung von Frauen und die Stärkung ihrer Rechte weltweit.',
    'Europatag': 'Erinnert an die Schuman-Erklärung 1950, den Grundstein der heutigen Europäischen Union.',
    'Internationaler Tag der Familie': 'Betont die Bedeutung der Familie als Grundeinheit der Gesellschaft und stärkt familienpolitische Initiativen.',
    'Welttag der kulturellen Vielfalt': 'Fördert den interkulturellen Dialog, Toleranz und das Verständnis für den Reichtum kultureller Identitäten.',
    'Weltumwelttag': 'Erinnert an die globale Verantwortung zur Erhaltung der Natur und zur Bewältigung ökologischer Krisen.',
    'Tag der Demokratie': 'Fördert die demokratischen Prinzipien, Bürgerbeteiligung und die Einhaltung der Menschenrechte weltweit.',
    'Weltkindertag': 'Setzt sich für die Rechte, den Schutz und das Wohlergehen aller Kinder ein.',
    'Weltfriedenstag': 'Tag des globalen Waffenstillstands und der Gewaltlosigkeit; erinnert an die Bedeutung der Friedenssicherung.',
    'Welttag zur Überwindung der Armut': 'Macht auf die weltweite Notlage extrem armer Menschen aufmerksam und fordert nachhaltige Solidarität.',
    'Internationaler Tag für Toleranz': 'Erinnert an die Wichtigkeit von gegenseitigem Respekt und Akzeptanz zwischen verschiedenen Kulturen und Glaubensgemeinschaften.',
    'Tag des Ehrenamts': 'Ehrt und fördert das freiwillige und unentgeltliche gesellschaftliche Engagement von Bürgern.',
    'Tag der Menschenrechte': 'Gedenktag zur Verkündung der Allgemeinen Erklärung der Menschenrechte im Jahr 1948 durch die Vereinten Nationen.'
  };

  // Wikipedia URLs Map
  const urls: Record<string, string> = {
    'Nationalfeiertag': 'https://de.wikipedia.org/wiki/Nationalfeiertag_(%C3%96sterreich)',
    'Silvester': 'https://de.wikipedia.org/wiki/Silvester',
    'Josefstag (Landespatron Tirol)': 'https://de.wikipedia.org/wiki/Josefstag',
    'Heilige Drei Könige': 'https://de.wikipedia.org/wiki/Erscheinung_des_Herrn',
    'Mariä Himmelfahrt': 'https://de.wikipedia.org/wiki/Mari%C3%A4_Himmelfahrt',
    'Allerheiligen': 'https://de.wikipedia.org/wiki/Allerheiligen',
    'Allerseelen': 'https://de.wikipedia.org/wiki/Allerseelen',
    'Mariä Empfängnis': 'https://de.wikipedia.org/wiki/Mari%C3%A4_Empf%C3%A4ngnis',
    'Heiliger Abend': 'https://de.wikipedia.org/wiki/Heiligabend',
    'Christtag': 'https://de.wikipedia.org/wiki/Erster_Weihnachtsfeiertag',
    'Stefanitag': 'https://de.wikipedia.org/wiki/Stefanitag',
    
    'Karfreitag': 'https://de.wikipedia.org/wiki/Karfreitag',
    'Ostersonntag': 'https://de.wikipedia.org/wiki/Ostersonntag',
    'Ostermontag': 'https://de.wikipedia.org/wiki/Ostermontag',
    'Christi Himmelfahrt': 'https://de.wikipedia.org/wiki/Christi_Himmelfahrt',
    'Pfingstmontag': 'https://de.wikipedia.org/wiki/Pfingstmontag',
    'Fronleichnam': 'https://de.wikipedia.org/wiki/Fronleichnam',
    
    'Ramadan (Beginn)': 'https://de.wikipedia.org/wiki/Ramadan',
    'Eid al-Fitr (Zuckerfest / Ende Ramadan)': 'https://de.wikipedia.org/wiki/Eid_al-Fitr',
    'Eid al-Adha (Opferfest)': 'https://de.wikipedia.org/wiki/Opferfest',
    'Islamisches Neujahr': 'https://de.wikipedia.org/wiki/Islamisches_Neujahr',
    
    'Pessach (Beginn)': 'https://de.wikipedia.org/wiki/Pessach',
    'Jom Kippur': 'https://de.wikipedia.org/wiki/Jom_Kippur',
    'Chanukka (Beginn)': 'https://de.wikipedia.org/wiki/Chanukka',
    
    'Vesakh (Buddhismus)': 'https://de.wikipedia.org/wiki/Vesakh',
    'Diwali (Hinduismus)': 'https://de.wikipedia.org/wiki/Diwali',
    
    'Neujahr': 'https://de.wikipedia.org/wiki/Neujahrstag',
    'Staatsfeiertag / Tag der Arbeit': 'https://de.wikipedia.org/wiki/Tag_der_Arbeit',
    
    'Opfer-Gedenktag (Holocaust-Gedenktag)': 'https://de.wikipedia.org/wiki/Internationaler_Tag_des_Gedenkens_an_die_Opfer_des_Holocaust',
    'Welttag der sozialen Gerechtigkeit': 'https://de.wikipedia.org/wiki/Welttag_der_sozialen_Gerechtigkeit',
    'Weltfrauentag': 'https://de.wikipedia.org/wiki/Weltfrauentag',
    'Europatag': 'https://de.wikipedia.org/wiki/Europatag',
    'Internationaler Tag der Familie': 'https://de.wikipedia.org/wiki/Internationaler_Tag_der_Familie',
    'Welttag der kulturellen Vielfalt': 'https://de.wikipedia.org/wiki/Welttag_der_kulturellen_Vielfalt_f%C3%BCr_Dialog_und_Entwicklung',
    'Weltumwelttag': 'https://de.wikipedia.org/wiki/Weltumwelttag',
    'Tag der Demokratie': 'https://de.wikipedia.org/wiki/Internationaler_Tag_der_Demokratie',
    'Weltkindertag': 'https://de.wikipedia.org/wiki/Weltkindertag',
    'Weltfriedenstag': 'https://de.wikipedia.org/wiki/Weltfriedenstag',
    'Welttag zur Überwindung der Armut': 'https://de.wikipedia.org/wiki/Welttag_zur_%C3%9Cberwindung_der_Armut',
    'Internationaler Tag für Toleranz': 'https://de.wikipedia.org/wiki/Internationaler_Tag_f%C3%BCr_Toleranz',
    'Tag des Ehrenamts': 'https://de.wikipedia.org/wiki/Internationaler_Tag_des_Ehrenamtes',
    'Tag der Menschenrechte': 'https://de.wikipedia.org/wiki/Tag_der_Menschenrechte'
  };

  const addH = (dateStr: string, name: string, category: Holiday['category']) => {
    holidays.push({
      dateStr,
      name,
      category,
      description: desc[name] || '',
      wikiUrl: urls[name] || ''
    });
  };

  // 1. Secular & Regional
  addH(`${year}-10-26`, 'Nationalfeiertag', 'secular');
  addH(`${year}-12-31`, 'Silvester', 'secular');
  addH(`${year}-03-19`, 'Josefstag (Landespatron Tirol)', 'secular');

  // 2. Christian
  addH(`${year}-01-06`, 'Heilige Drei Könige', 'christian');
  addH(`${year}-08-15`, 'Mariä Himmelfahrt', 'christian');
  addH(`${year}-11-01`, 'Allerheiligen', 'christian');
  addH(`${year}-11-02`, 'Allerseelen', 'christian');
  addH(`${year}-12-08`, 'Mariä Empfängnis', 'christian');
  addH(`${year}-12-24`, 'Heiliger Abend', 'christian');
  addH(`${year}-12-25`, 'Christtag', 'christian');
  addH(`${year}-12-26`, 'Stefanitag', 'christian');

  // Easter-based holidays
  const easter = getEasterSunday(year);

  const addDays = (baseDate: Date, days: number): string => {
    const d = new Date(baseDate.getTime());
    d.setDate(baseDate.getDate() + days);
    
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayVal = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dayVal}`;
  };

  addH(addDays(easter, -2), 'Karfreitag', 'christian');
  addH(addDays(easter, 0), 'Ostersonntag', 'christian');
  addH(addDays(easter, 1), 'Ostermontag', 'christian');
  addH(addDays(easter, 39), 'Christi Himmelfahrt', 'christian');
  addH(addDays(easter, 50), 'Pfingstmontag', 'christian');
  addH(addDays(easter, 60), 'Fronleichnam', 'christian');

  // 3. Islamic (Gregorian lookup dates 2024-2030)
  const islamicHolidaysByYear: Record<number, { name: string; dateStr: string }[]> = {
    2024: [
      { name: 'Ramadan (Beginn)', dateStr: '2024-03-10' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2024-04-10' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2024-06-16' },
      { name: 'Islamisches Neujahr', dateStr: '2024-07-07' }
    ],
    2025: [
      { name: 'Ramadan (Beginn)', dateStr: '2025-02-28' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2025-03-30' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2025-06-06' },
      { name: 'Islamisches Neujahr', dateStr: '2025-06-26' }
    ],
    2026: [
      { name: 'Ramadan (Beginn)', dateStr: '2026-02-18' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2026-03-20' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2026-05-27' },
      { name: 'Islamisches Neujahr', dateStr: '2026-06-16' }
    ],
    2027: [
      { name: 'Ramadan (Beginn)', dateStr: '2027-02-08' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2027-03-09' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2027-05-17' },
      { name: 'Islamisches Neujahr', dateStr: '2027-06-06' }
    ],
    2028: [
      { name: 'Ramadan (Beginn)', dateStr: '2028-01-28' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2028-02-26' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2028-05-05' },
      { name: 'Islamisches Neujahr', dateStr: '2028-05-25' }
    ],
    2029: [
      { name: 'Ramadan (Beginn)', dateStr: '2029-01-16' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2029-02-14' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2029-04-24' },
      { name: 'Islamisches Neujahr', dateStr: '2029-05-14' }
    ],
    2030: [
      { name: 'Ramadan (Beginn)', dateStr: '2030-01-05' },
      { name: 'Eid al-Fitr (Zuckerfest / Ende Ramadan)', dateStr: '2030-02-04' },
      { name: 'Eid al-Adha (Opferfest)', dateStr: '2030-04-14' },
      { name: 'Islamisches Neujahr', dateStr: '2030-05-03' }
    ]
  };

  if (islamicHolidaysByYear[year]) {
    islamicHolidaysByYear[year].forEach(h => {
      addH(h.dateStr, h.name, 'islamic');
    });
  }

  // 4. Jewish (Gregorian lookup dates 2024-2030)
  const jewishHolidaysByYear: Record<number, { name: string; dateStr: string }[]> = {
    2024: [
      { name: 'Pessach (Beginn)', dateStr: '2024-04-22' },
      { name: 'Jom Kippur', dateStr: '2024-10-12' },
      { name: 'Chanukka (Beginn)', dateStr: '2024-12-25' }
    ],
    2025: [
      { name: 'Pessach (Beginn)', dateStr: '2025-04-12' },
      { name: 'Jom Kippur', dateStr: '2025-10-02' },
      { name: 'Chanukka (Beginn)', dateStr: '2025-12-14' }
    ],
    2026: [
      { name: 'Pessach (Beginn)', dateStr: '2026-04-02' },
      { name: 'Jom Kippur', dateStr: '2026-10-21' },
      { name: 'Chanukka (Beginn)', dateStr: '2026-12-04' }
    ],
    2027: [
      { name: 'Pessach (Beginn)', dateStr: '2027-04-21' },
      { name: 'Jom Kippur', dateStr: '2027-10-11' },
      { name: 'Chanukka (Beginn)', dateStr: '2027-12-24' }
    ],
    2028: [
      { name: 'Pessach (Beginn)', dateStr: '2028-04-10' },
      { name: 'Jom Kippur', dateStr: '2028-09-30' },
      { name: 'Chanukka (Beginn)', dateStr: '2028-12-12' }
    ],
    2029: [
      { name: 'Pessach (Beginn)', dateStr: '2029-03-30' },
      { name: 'Jom Kippur', dateStr: '2029-09-19' },
      { name: 'Chanukka (Beginn)', dateStr: '2029-12-02' }
    ],
    2030: [
      { name: 'Pessach (Beginn)', dateStr: '2030-04-17' },
      { name: 'Jom Kippur', dateStr: '2030-10-07' },
      { name: 'Chanukka (Beginn)', dateStr: '2030-12-21' }
    ]
  };

  if (jewishHolidaysByYear[year]) {
    jewishHolidaysByYear[year].forEach(h => {
      addH(h.dateStr, h.name, 'jewish');
    });
  }

  // 5. Other World Religions (Diwali, Vesakh 2024-2030)
  const otherReligionsByYear: Record<number, { name: string; dateStr: string }[]> = {
    2024: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2024-05-23' },
      { name: 'Diwali (Hinduismus)', dateStr: '2024-10-31' }
    ],
    2025: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2025-05-12' },
      { name: 'Diwali (Hinduismus)', dateStr: '2025-10-20' }
    ],
    2026: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2026-06-01' },
      { name: 'Diwali (Hinduismus)', dateStr: '2026-11-08' }
    ],
    2027: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2027-05-20' },
      { name: 'Diwali (Hinduismus)', dateStr: '2027-10-29' }
    ],
    2028: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2028-05-08' },
      { name: 'Diwali (Hinduismus)', dateStr: '2028-10-17' }
    ],
    2029: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2029-05-27' },
      { name: 'Diwali (Hinduismus)', dateStr: '2029-11-05' }
    ],
    2030: [
      { name: 'Vesakh (Buddhismus)', dateStr: '2030-05-16' },
      { name: 'Diwali (Hinduismus)', dateStr: '2030-10-26' }
    ]
  };

  if (otherReligionsByYear[year]) {
    otherReligionsByYear[year].forEach(h => {
      addH(h.dateStr, h.name, 'other_religions');
    });
  }

  // 6. International
  addH(`${year}-01-01`, 'Neujahr', 'international');
  addH(`${year}-05-01`, 'Staatsfeiertag / Tag der Arbeit', 'international');

  // 7. Themenbezogene Tage (Development & Special Days)
  addH(`${year}-01-27`, 'Opfer-Gedenktag (Holocaust-Gedenktag)', 'development');
  addH(`${year}-02-20`, 'Welttag der sozialen Gerechtigkeit', 'development');
  addH(`${year}-03-08`, 'Weltfrauentag', 'development');
  addH(`${year}-05-09`, 'Europatag', 'development');
  addH(`${year}-05-15`, 'Internationaler Tag der Familie', 'development');
  addH(`${year}-05-21`, 'Welttag der kulturellen Vielfalt', 'development');
  addH(`${year}-06-05`, 'Weltumwelttag', 'development');
  addH(`${year}-09-15`, 'Tag der Demokratie', 'development');
  addH(`${year}-09-20`, 'Weltkindertag', 'development');
  addH(`${year}-09-21`, 'Weltfriedenstag', 'development');
  addH(`${year}-10-17`, 'Welttag zur Überwindung der Armut', 'development');
  addH(`${year}-11-16`, 'Internationaler Tag für Toleranz', 'development');
  addH(`${year}-12-05`, 'Tag des Ehrenamts', 'development');
  addH(`${year}-12-10`, 'Tag der Menschenrechte', 'development');

  return holidays;
}
